import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Chip, Collapse, IconButton, Divider, Avatar } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getStatusColor, hexToRgba, getPriorityColor } from '../../dashboard/calendar-view/calendarColorUtils';
import { stringToColor } from '../../../shared/commonFunctions';
import SafeCommentRenderer from '../../../shared/SafeCommentRenderer';
import { prepareContentForEditing, decodeHtmlContent } from '../../../shared/htmlUtils';

const getInitials = (name) => {
  if (!name) return 'U';
  return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
};

const isActivationKey = (event) => event.key === 'Enter' || event.key === ' ';

// Function to get formatted date and time for comments
const getCommentDateTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';

  const dateText = date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
  const timeText = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });

  return `${dateText}, ${timeText}`;
};

const BlockerRow = ({ blocker }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  
  // Support object payloads for status
  const displayStatus = typeof blocker.status === 'object' && blocker.status !== null 
    ? blocker.status 
    : (blocker.status || 'Pending');
  
  const statusLabel = typeof displayStatus === 'object' ? displayStatus.name : displayStatus;

  const ticketTitle = blocker.ticket_title || blocker.title || '';
  const hasComment = !!(blocker.latest_comment || blocker.comment);
  const rawCommentText = hasComment 
    ? (blocker.latest_comment || blocker.comment) 
    : (t('dailyStandup.blockersRisks.noComments') || 'No comments added to this ticket.');
  const commentText = hasComment 
    ? decodeHtmlContent(prepareContentForEditing(rawCommentText)) 
    : rawCommentText;
  const commentTime = getCommentDateTime(blocker.latest_comment_at);

  return (
    <Box className="blocker-row-container">
      <Box 
        className={`blocker-row ${expanded ? 'expanded' : ''}`}
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(event) => {
          if (isActivationKey(event)) {
            event.preventDefault();
            setExpanded(!expanded);
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
      >
        <IconButton size="small" className="expand-btn">
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
        
        <Box className="priority-indicator-container">
          <Box 
            className="priority-circle" 
            style={{ 
              backgroundColor: blocker.priority 
                ? getPriorityColor(blocker.priority) 
                : getStatusColor(displayStatus) 
            }} 
          />
        </Box>
        
        <Box className="ticket-details">
          <Typography className="ticket-number">
            {blocker.ticket_key}
          </Typography>
          <Typography className="ticket-title">
            {ticketTitle}
          </Typography>
        </Box>
        
        <Box className="chips-container">
          <Chip 
            label={statusLabel} 
            size="small" 
            className="status-chip"
            sx={{ 
              backgroundColor: hexToRgba(getStatusColor(displayStatus), 0.08) + ' !important', 
              color: getStatusColor(displayStatus) + ' !important',
              border: `1px solid ${hexToRgba(getStatusColor(displayStatus), 0.18)} !important`
            }}
          />
        </Box>
      </Box>
      
      <Collapse in={expanded}>
        <Box className="blocker-divider-container">
          <Divider />
        </Box>
        <Box className="blocker-comment-container">
          <Box className={`comment-text ${!hasComment ? 'no-comment' : ''}`}>
            {hasComment ? (
              <Box className="comment-content">
                <SafeCommentRenderer content={commentText} />
                {commentTime && (
                  <Typography className="blocker-comment-timestamp">
                    {commentTime}
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                {commentText}
              </Typography>
            )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

const AssigneeGroup = ({ assigneeName, blockers, showCount = false }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <Box className="assignee-group-container">
      <Box 
        className="assignee-group-header" 
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(event) => {
          if (isActivationKey(event)) {
            event.preventDefault();
            setExpanded(!expanded);
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
      >
        <Box className="assignee-group-header-left">
          <Avatar 
            className="assignee-avatar"
            sx={{ width: 28, height: 28, bgcolor: stringToColor(assigneeName) }}
          >
            {getInitials(assigneeName)}
          </Avatar>
          <Typography className="assignee-name">
            {assigneeName}
          </Typography>
        </Box>
        <Box className="assignee-group-header-right" display="flex" alignItems="center" gap={1}>
          {showCount && (
            <span className="user-item-count-badge">
              {blockers.length} {blockers.length === 1 ? 'Item' : 'Items'}
            </span>
          )}
          <IconButton size="small" className="expand-btn">
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Box>
      <Collapse in={expanded}>
        <Box className="assignee-group-content">
          {blockers.map((blocker, idx) => (
            <BlockerRow key={idx} blocker={blocker} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

const BlockersRisks = ({ blockers = [], disableGrouping = false, showCount = false }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  // Group blockers by assignee
  const groupedBlockers = blockers.reduce((acc, blocker) => {
    const owner = blocker.owner || 'Unassigned';
    if (!acc[owner]) acc[owner] = [];
    acc[owner].push(blocker);
    return acc;
  }, {});

  return (
    <Box className="blockers-risks-container">
      <Box 
        className="blockers-header"
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(event) => {
          if (isActivationKey(event)) {
            event.preventDefault();
            setExpanded(!expanded);
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
      >
        <Typography className="section-title">
          {t('dailyStandup.blockersRisks.title')}
        </Typography>
        <IconButton size="small" className="header-icon-btn">
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box className="blockers-content">
          {blockers.length === 0 ? (
            <Box className="empty-card-state">
              <InfoOutlinedIcon className="empty-icon" />
              <Typography className="empty-text">
                {t('dailyStandup.blockersRisks.noBlockers')}
              </Typography>
            </Box>
          ) : disableGrouping ? (
            <Box className="blockers-list">
              {blockers.map((blocker, idx) => (
                <BlockerRow key={idx} blocker={blocker} />
              ))}
            </Box>
          ) : (
            <Box className="assignee-cards-container">
              {Object.entries(groupedBlockers).map(([assigneeName, assigneeBlockers], idx) => (
                <AssigneeGroup key={idx} assigneeName={assigneeName} blockers={assigneeBlockers} showCount={showCount} />
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default BlockersRisks;
